import { Phone, Mail, ArrowRight } from "lucide-react";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { Breadcrumbs } from "@/components/site/Breadcrumbs";
import { PageHeroCard } from "@/components/site/PageHeroCard";
import { Button } from "@/components/ui/button";
import { useLang } from "@/i18n/LangContext";
import faqHero from "@/assets/faq-hero.jpg";
import { PageMeta } from "@/components/site/PageMeta";

const Faq = () => {
  const { t, lang } = useLang();


  const title =
    lang === "sv"
      ? "Frågor och svar | Intygsläkaren"
      : "FAQ | Intygsläkaren";
  const desc =
    lang === "sv"
      ? "Vanliga frågor om läkarintyg, bokning, kostnader och procedurer hos Intygsläkaren i Göteborg."
      : "Frequently asked questions about medical certificates, booking, costs and procedures at Intygsläkaren in Gothenburg.";

  const bookingHref = lang === "sv" ? "/boka-tid" : "/en/book-appointment";
  const contactHref = lang === "sv" ? "/kontakt" : "/en/contact";

  return (
    <div className="min-h-screen bg-background">
      <PageMeta title={title} description={desc} />
      <Header />
      <main>
        {/* Hero */}
        <PageHeroCard imageSrc={faqHero} imageAlt={lang === "sv" ? "Läkare svarar på frågor" : "Doctor answering questions"} compact>
          <div className="animate-fade-up max-w-2xl">
            <h1 className="text-balance font-display text-4xl font-extrabold leading-[1.05] tracking-tight text-foreground sm:text-5xl lg:text-6xl">
              {t.faq.title}
            </h1>
            <p className="mt-5 max-w-xl text-base leading-relaxed text-muted-foreground sm:text-lg">
              {t.faq.subtitle}
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Button asChild size="xl" variant="hero">
                <a href={bookingHref}>
                  {t.faq.bookCta}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </a>
              </Button>
              <Button asChild size="xl" variant="ghost-outline">
                <a href={contactHref}>{t.faq.contactCta}</a>
              </Button>
            </div>
          </div>
        </PageHeroCard>

        {/* FAQ sections */}
        <section className="py-14 lg:py-20">
          <div className="container-wide">
            <div className="mx-auto grid max-w-6xl gap-12 lg:grid-cols-2">
              {t.faq.sections.map((section) => (
                <div key={section.title}>
                  <h2 className="font-display text-2xl font-semibold tracking-tight text-foreground sm:text-3xl">
                    {section.title}
                  </h2>
                  <div className="mt-4 grid gap-4">
                    {section.items.map((item, idx) => (
                      <article key={idx} className="rounded-2xl border border-border/60 bg-card p-4 sm:p-5">
                        <h3 className="text-base font-semibold text-foreground sm:text-lg">{item.q}</h3>
                        <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{item.a}</p>
                      </article>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Still have questions CTA */}
        <section className="px-2 py-16 sm:px-3 lg:px-4 lg:py-20">
          <div className="relative mx-auto w-full max-w-[96rem] overflow-hidden rounded-2xl bg-primary px-8 py-14 text-primary-foreground shadow-elevated sm:rounded-3xl lg:px-16 lg:py-20">
              <div className="absolute -right-20 -top-20 h-72 w-72 rounded-full bg-white/10 blur-3xl" />
              <div className="absolute -bottom-24 -left-10 h-72 w-72 rounded-full bg-white/10 blur-3xl" />
              <div className="relative max-w-2xl">
                <h2 className="font-display text-3xl font-semibold tracking-tight sm:text-4xl">
                  {t.faq.stillTitle}
                </h2>
                <p className="mt-3 text-base text-primary-foreground/85">{t.faq.stillText}</p>
                <div className="mt-7 flex flex-wrap gap-3">
                  <Button asChild variant="onPrimary" size="xl">
                    <a href="tel:+46317617700">
                      <Phone className="mr-2 h-4 w-4" />
                      {t.cta.secondary}
                    </a>
                  </Button>
                  <Button asChild variant="ghostOnPrimary" size="xl">
                    <a href={contactHref}>
                      <Mail className="mr-2 h-4 w-4" />
                      {t.faq.contactCta}
                    </a>
                  </Button>
                </div>
              </div>
            </div>
        </section>
        <Breadcrumbs
          items={[
            { label: lang === "sv" ? "Hem" : "Home", to: lang === "sv" ? "/" : "/en" },
            { label: t.nav.faq },
          ]}
        />
      </main>
      <Footer />
    </div>
  );
};

export default Faq;